import React, { useMemo, useState } from 'react';
import axios from 'axios';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { Button, ButtonToolbar, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

import { ThemeProps, RTLProps } from '@/shared/prop-types/ReducerProps';

import ProgramViewUserProfile from './View/profile';
import ProgramViewUserHistory from './View/history';
import ProgramViewUserGiftCodesRedeemed from './View/giftCodesRedeemed';
import ProgramViewUserChangeLogs from './View/changeLogs';
import PointsAwarded from './View/PointsAwarded';
import { ReclaimPoints } from './View/ReclaimPoints';
import EventHistory from './View/EventHistory';
import ProgramRoles from './View/ProgramRoles';
import { USER_VIEW_TAB_VISIBILITY } from './userViewTabVisibility';

import './style.scss';
import '../components/style.scss';

const ViewProgramUserModalContent = ({ organization, program, userid, isOpen, toggle, theme, rtl }) => {
  const [activeTab, setActiveTab] = useState('1');
  const tabToggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const fetchUserDetails = async () => {
    const response = await axios.get(
      `/organization/${program.organization_id}/program/${program.id}/user/${userid}`
    );

    return response.data;
  };

  const { data, isLoading, error } = useQuery(
    ['program-user-detail-modal', program?.organization_id, program?.id, userid],
    fetchUserDetails,
    {
      enabled: Boolean(isOpen && program?.id && userid),
      keepPreviousData: false,
      staleTime: Infinity,
    }
  );

  const fullName = data ? `${data.first_name || ''} ${data.last_name || ''}`.trim() : 'View User';
  const subtitle = data?.email
    ? `${data.email}${data?.status?.status ? ` • ${data.status.status}` : ''}`
    : 'Review profile, activity, awards, and events for this user.';

  return (
    <Modal
      className={`modal-program manager-program-modal manager-program-modal--wide modal-xl ${theme.className} ${rtl.direction}-support`}
      isOpen={isOpen}
      toggle={toggle}
      style={{ width: '1024px' }}
    >
      <ModalHeader tag="div" className="manager-program-modal__header">
        <div className="manager-program-modal__header-row">
          <div className="manager-program-modal__header-copy">
            <h3 className="manager-program-modal__title">{fullName || 'View User'}</h3>
            <p className="manager-program-modal__subtitle">{subtitle}</p>
          </div>
          <ButtonToolbar className="manager-program-modal__actions">
            <Button outline color="primary" onClick={toggle}>Close</Button>
          </ButtonToolbar>
        </div>
      </ModalHeader>
      <ModalBody className="manager-program-modal__body">
        {isLoading && <p>Loading user details...</p>}
        {error && <p>Unable to load user details.</p>}
        {!isLoading && !error && data && (
          <div className="manager-program-user-view">
            <div className="manager-program-modal__tab-shell">
              <Nav tabs className="manager-program-modal__tabs manager-program-user-view__tabs">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '1' })}
                    onClick={() => tabToggle('1')}
                  >
                    Profile
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '2' })}
                    onClick={() => tabToggle('2')}
                  >
                    Activity
                  </NavLink>
                </NavItem>
                {USER_VIEW_TAB_VISIBILITY.giftCodesRedeemed && (
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '3' })}
                      onClick={() => tabToggle('3')}
                    >
                      Gift Codes Redeemed
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '4' })}
                    onClick={() => tabToggle('4')}
                  >
                    Audit Log
                  </NavLink>
                </NavItem>
                {USER_VIEW_TAB_VISIBILITY.reclaimPoints && (
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '5' })}
                      onClick={() => tabToggle('5')}
                    >
                      Reclaim Points
                    </NavLink>
                  </NavItem>
                )}
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '8' })}
                    onClick={() => tabToggle('8')}
                  >
                    Program Roles
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '6' })}
                    onClick={() => tabToggle('6')}
                  >
                    Awards
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '7' })}
                    onClick={() => tabToggle('7')}
                  >
                    Events
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="manager-program-modal__tab-panel manager-program-user-view__tab-panel">
                <TabPane tabId="1">
                  <ProgramViewUserProfile
                    data={data}
                    organization={organization}
                    program={program}
                    programId={program.id}
                    userId={userid}
                  />
                </TabPane>
                <TabPane tabId="2">
                  {activeTab !== '2'
                    ? 'Loading...'
                    : <ProgramViewUserHistory user={data} organization={organization} program={program} />}
                </TabPane>
                {USER_VIEW_TAB_VISIBILITY.giftCodesRedeemed && (
                  <TabPane tabId="3">
                    {activeTab !== '3'
                      ? 'Loading...'
                      : <ProgramViewUserGiftCodesRedeemed user={data} organization={organization} program={program} />}
                  </TabPane>
                )}
                <TabPane tabId="4">
                  {activeTab !== '4'
                    ? 'Loading...'
                    : <ProgramViewUserChangeLogs user={data} organization={organization} program={program} />}
                </TabPane>
                {USER_VIEW_TAB_VISIBILITY.reclaimPoints && (
                  <TabPane tabId="5">
                    {activeTab !== '5'
                      ? 'Loading...'
                      : <ReclaimPoints user={data} organization={organization} program={program} />}
                  </TabPane>
                )}
                <TabPane tabId="6">
                  {activeTab !== '6'
                    ? 'Loading...'
                    : <PointsAwarded user={data} organization={organization} program={program} />}
                </TabPane>
                <TabPane tabId="7">
                  {activeTab !== '7'
                    ? 'Loading...'
                    : <EventHistory user={data} organization={organization} program={program} />}
                </TabPane>
                <TabPane tabId="8">
                  {activeTab !== '8'
                    ? 'Loading...'
                    : <ProgramRoles organization={organization} user={data} />}
                </TabPane>
              </TabContent>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

ViewProgramUserModalContent.propTypes = {
  theme: ThemeProps.isRequired,
  rtl: RTLProps.isRequired,
};

const ConnectedViewProgramUserModalContent = connect((state) => ({
  theme: state.theme,
  rtl: state.rtl,
}))(ViewProgramUserModalContent);

const ViewProgramUserModal = (props) => {
  const queryClient = useMemo(() => new QueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ConnectedViewProgramUserModalContent {...props} />
    </QueryClientProvider>
  );
};

export default ViewProgramUserModal;
